import React from 'react';
import {
  Box,
  Flex,
  HStack,
  Button,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  VStack,
  Image,
  Divider,
} from '@chakra-ui/react';
import { FiChevronDown, FiMenu, FiLogOut } from 'react-icons/fi';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import keycloakClient from '../Keycloak';
import { apiService } from '../ExternalApiService';
import logoImage from '../assets/SmartSend_Logo.png';
// removed global route transition animations

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const bg = useColorModeValue('white', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const activeColor = '#92D600';

  const menuItems = [
    { name: 'Dashboard', path: '/dashboard' },
    { name: 'Templates', path: '/dashboard/templates' },
    { name: 'Messages', path: '/dashboard/messages' },
    { name: 'Billing', path: '/dashboard/billing' },
  ];

  const handleLogout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      if (process.env.NODE_ENV !== 'test') {
        console.error('Failed to invalidate backend session', error);
      }
    } finally {
      keycloakClient.logout();
    }
  };

  const getUserInfo = () => {
    if (keycloakClient.authenticated && keycloakClient.tokenParsed) {
      const { name, given_name, family_name, email } = keycloakClient.tokenParsed;
      return {
        name: name || `${given_name || ''} ${family_name || ''}`.trim() || 'User',
        email: email || ''
      };
    }
    return { name: 'User', email: '' };
  };

  const userInfo = getUserInfo();

  const MobileMenu = () => (
    <Drawer isOpen={isOpen} onClose={onClose} placement="top">
      <DrawerOverlay />
      <DrawerContent>
        <VStack spacing={4} p={4}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Button
                key={item.name}
                variant="ghost"
                color={isActive ? activeColor : textColor}
                onClick={() => {
                  navigate(item.path);
                  onClose();
                }}
                w="full"
                justifyContent="flex-start"
              >
                {item.name}
              </Button>
            );
          })}
          <Button
            variant="ghost"
            color="red.500"
            onClick={handleLogout}
            w="full"
            justifyContent="flex-start"
            leftIcon={<FiLogOut />}
          >
            Logout
          </Button>
        </VStack>
      </DrawerContent>
    </Drawer>
  );

  return (
    <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
      <Box
        bg={bg}
        borderBottom="1px"
        borderBottomColor={borderColor}
        px={6}
        py={4}
        position="sticky"
        top={0}
        zIndex={10}
        h={16}
      >
        <Flex justify="space-between" align="center" h="full">
          <HStack spacing={4} h="full" align="center">
            <Image
              src={logoImage}
              alt="Smart Send Logo"
              h={6}
              w="auto"
              __css={{marginRight: '48px'}}
            />

            <Divider orientation="vertical" h={16} borderColor="gray.300" />

            <HStack spacing={8} display={{ base: 'none', md: 'flex' }}>
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Button
                    key={item.name}
                    variant="ghost"
                    color={isActive ? activeColor : textColor}
                    fontWeight={isActive ? 'semibold' : 'normal'}
                    onClick={() => navigate(item.path)}
                    _hover={{ color: activeColor }}
                    px={0}
                    py={2}
                    h="auto"
                    fontSize="md"
                  >
                    {item.name}
                  </Button>
                );
              })}
            </HStack>
          </HStack>

          <HStack spacing={4}>
            <Divider orientation="vertical" h={16} borderColor="gray.300" />

            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                rightIcon={<FiChevronDown />}
                leftIcon={<Avatar size="sm" name={userInfo.name} color={'white'} background={activeColor} />}
                color={activeColor}
                fontWeight="medium"
                _hover={{ bg: 'gray.50' }}
              >
                {userInfo.name}
              </MenuButton>
              <MenuList>
                <MenuItem icon={<FiLogOut />} onClick={handleLogout} color="red.500">
                  Logout
                </MenuItem>
              </MenuList>
            </Menu>

            <IconButton
              display={{ base: 'flex', md: 'none' }}
              onClick={onOpen}
              variant="ghost"
              aria-label="open menu"
              icon={<FiMenu />}
            />
          </HStack>
        </Flex>
      </Box>

      <MobileMenu />

      <Box p={6}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default DashboardLayout;
